'use client';

/**
 * Account switcher in stile Facebook per admin charter.
 *
 * Dropdown sempre visibile quando l'utente loggato è admin (anche durante
 * un impersonate). Permette di passare istantaneamente tra:
 *  - "Tu (admin)" — termina impersonate (cancella cookie imp_target)
 *  - I 10 utenti più impersonati di recente — set cookie + redirect
 *
 * La sessione admin resta SEMPRE attiva: cambia solo il cookie imp_target.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, ShieldCheck, UserCog, ArrowRight, Search, Loader2, Building2 } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { authService, RecentImpersonatedUser } from '@/services/authService';

const ROLE_BADGE: Record<string, string> = {
  CUSTOMER: 'Cliente',
  AGENCY: 'Agenzia',
  COLLABORATOR: 'Collaboratore',
  VENDOR: 'Vendor',
  COMPANY: 'Azienda',
  SERVICE_PROVIDER: 'Partner',
  ADMIN: 'Admin',
};

interface AccountSwitcherProps {
  /**
   * - `default`: bottone compatto con border, padding piccolo (per floating).
   * - `sidebar`: bottone full-width, padding più ampio, allineato per integrarsi
   *   nell'header della sidebar admin (riga sotto è actions).
   */
  variant?: 'default' | 'sidebar';
}

export default function AccountSwitcher({ variant = 'default' }: AccountSwitcherProps = {}) {
  const { user, impersonating, startImpersonate, stopImpersonate } = useAuth();
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<RecentImpersonatedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  // Click-outside per chiusura: il menu ora è in Portal, quindi controlliamo
  // sia trigger sia menu separatamente.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const inTrigger = ref.current && ref.current.contains(e.target as Node);
      const inMenu = menuRef.current && menuRef.current.contains(e.target as Node);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Calcolo posizione menu (fixed) al click del trigger + reposition su resize/scroll
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const compute = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      if (variant === 'sidebar') {
        setMenuPos({ top: r.top, left: r.right + 8 });
      } else {
        setMenuPos({ top: r.bottom + 8, left: r.right - 320 });
      }
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, variant]);

  const loadRecent = useCallback(async () => {
    if (recent.length > 0) return;
    setLoading(true);
    try {
      const data = await authService.getImpersonateRecent();
      setRecent(data);
    } finally {
      setLoading(false);
    }
  }, [recent.length]);

  // Mostra il dropdown solo se l'admin reale è loggato.
  // Quando si impersona, `user` è il target e `impersonating` ha info admin.
  // Se non c'è nessun admin reale (utente non admin e non in impersonate),
  // il componente non si mostra.
  const isAdminReal = user?.role === 'ADMIN' || !!impersonating;
  const isSidebar = variant === 'sidebar';

  // Vendor/Company loggato diretto (no admin, no impersonate): niente switch,
  // ma mostra l'identità AZIENDA in alto a sx (come richiesto), non null.
  if (!isAdminReal) {
    const companyName = user?.company_name || user?.name || user?.email || 'Azienda';
    const sub = user?.company_name ? (user?.name || user?.email || '') : (user?.email || '');
    return (
      <div
        data-cid="account-identity"
        className={isSidebar
          ? 'w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-white border-slate-200 text-slate-700'
          : 'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-white border-slate-200 text-xs font-bold text-slate-700'}
      >
        <div className={isSidebar ? 'shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900 text-white' : ''}>
          <Building2 className="w-4 h-4" />
        </div>
        <div className={`text-left leading-tight min-w-0 flex-1 ${isSidebar ? '' : 'hidden sm:block'}`}>
          <div className={isSidebar ? 'text-xs font-bold truncate' : 'text-[11px] truncate max-w-[140px]'}>{companyName}</div>
          {sub && <div className={isSidebar ? 'text-[10px] opacity-60 truncate' : 'text-[9px] opacity-70 truncate max-w-[140px]'}>{sub}</div>}
        </div>
      </div>
    );
  }

  const handleOpen = () => {
    setOpen((v) => !v);
    void loadRecent();
  };

  const switchTo = async (targetId: string) => {
    setBusy(targetId);
    try {
      await startImpersonate(targetId);
    } catch (e) {
      console.error('Switch failed', e);
      setBusy(null);
    }
  };

  const stop = async () => {
    setBusy('admin');
    try {
      await stopImpersonate();
    } catch (e) {
      console.error('Stop failed', e);
      setBusy(null);
    }
  };

  const headerLabel = impersonating
    ? user?.company_name || user?.name || user?.email || 'Utente'
    : 'Tu (admin)';
  const headerSubtitle = impersonating
    ? `admin: ${impersonating.admin_email}`
    : user?.email || '';

  return (
    <div className="relative" ref={ref} data-cid="account-switcher">
      <button
        ref={triggerRef}
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        data-cid="account-switcher-trigger"
        className={
          isSidebar
            ? `w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-colors ${
                impersonating
                  ? 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200/70'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`
            : `inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold transition-colors ${
                impersonating
                  ? 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200/70'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`
        }
      >
        <div className={`shrink-0 ${isSidebar ? 'w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900 text-white' : ''}`}>
          {impersonating ? (
            <UserCog className={isSidebar ? 'w-4 h-4' : 'w-4 h-4'} />
          ) : (
            <ShieldCheck className={isSidebar ? 'w-4 h-4 text-emerald-300' : 'w-4 h-4 text-emerald-500'} />
          )}
        </div>
        <div className={`text-left leading-tight min-w-0 flex-1 ${isSidebar ? '' : 'hidden sm:block'}`}>
          <div className={isSidebar ? 'text-xs font-bold truncate' : 'text-[11px] truncate max-w-[140px]'}>{headerLabel}</div>
          <div className={isSidebar ? 'text-[10px] opacity-60 truncate' : 'text-[9px] opacity-70 truncate max-w-[140px]'}>{headerSubtitle}</div>
        </div>
        <ChevronDown className={`shrink-0 w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && menuPos && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-cid="account-switcher-menu"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 2147483647 }}
          className="w-80 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
        >
          {/* Item: torna admin (CTA primario quando impersonating) */}
          <button
            onClick={stop}
            disabled={busy === 'admin' || !impersonating}
            className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
              impersonating
                ? 'bg-emerald-500/10 hover:bg-emerald-500/20 border-b border-emerald-200'
                : 'bg-emerald-50'
            } disabled:opacity-50`}
          >
            <ShieldCheck className="w-5 h-5 mt-0.5 text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900">
                {impersonating ? 'Torna come admin' : 'Tu (admin)'}
              </p>
              <p className="text-[11px] text-slate-500 truncate">
                {impersonating?.admin_email || user?.email}
              </p>
            </div>
            {!impersonating && <Check className="w-4 h-4 text-emerald-600 mt-1" />}
            {busy === 'admin' && <Loader2 className="w-4 h-4 mt-1 animate-spin text-emerald-600" />}
          </button>

          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/60">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Utenti recenti
            </p>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-3 text-xs text-slate-400 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Caricamento…
              </div>
            ) : recent.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-400 italic">
                Nessuno ancora — usa &quot;Cerca utente&quot; sotto.
              </div>
            ) : (
              recent.map((r) => {
                const isCurrent = impersonating && user?.email === r.email;
                const displayName = r.company_name || r.name || r.email;
                return (
                  <button
                    key={r.id}
                    onClick={() => switchTo(r.id)}
                    disabled={busy === r.id}
                    className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                      isCurrent ? 'bg-amber-50' : 'hover:bg-slate-50'
                    } disabled:opacity-50`}
                  >
                    <UserCog className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {displayName}
                      </p>
                      <p className="text-[10px] text-slate-500 truncate">
                        {ROLE_BADGE[r.role] || r.role} · {formatRel(r.last_impersonated_at)}
                      </p>
                    </div>
                    {isCurrent && <Check className="w-4 h-4 text-amber-600 mt-1" />}
                    {busy === r.id && <Loader2 className="w-4 h-4 mt-1 animate-spin text-amber-600" />}
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t border-slate-100">
            <a
              href="/admin/manage?tab=clientManagement"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              <Search className="w-4 h-4" />
              Cerca utente
              <ArrowRight className="w-3 h-3 ml-auto" />
            </a>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function formatRel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'ora';
  if (minutes < 60) return `${minutes}m fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}g fa`;
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}
